import { useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import PropTypes from "prop-types";
import AudioWaveform from "../Carousel/Carousel";
import "./horizontalScroll.css";

const HorizontalScrollDisplay = ({ medias, postData, onAddComment }) => {
  const [selectedMedia, setSelectedMedia] = useState(medias[0] || null);
  const scrollContainerRef = useRef(null);

  const handleMediaClick = (media, event) => {
    event.stopPropagation(); // Prevent popup
    setSelectedMedia(media);
  };

  const scroll = (direction, event) => {
    event.stopPropagation(); // Prevent popup
    const container = scrollContainerRef.current;
    if (!container) return;

    const thumbnailWidth = container.querySelector('.hs-thumbnail-item')?.offsetWidth || 150;
    const gap = 16; // Gap between thumbnails
    const scrollAmount = thumbnailWidth + gap;

    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  // Prevent popup when clicking inside primary media
  const handlePrimaryClick = (event) => {
    event.stopPropagation();
  };

  // Render media based on type
  const renderPrimaryMedia = (media) => {
    if (!media) return null;

    switch (media.mediatype) {
      case "image":
        return (
          <img 
            src={media.mediaurl} 
            alt={media.alt || "Primary media"} 
            className="hs-primary-media"
            onClick={handlePrimaryClick}
          />
        );
      case "video":
        return (
          <video 
            src={media.mediaurl} 
            className="hs-primary-media" 
            controls 
            playsInline
            onClick={handlePrimaryClick}
          />
        );
      case "audio":
        return (
          <div className="hs-primary-audio" onClick={handlePrimaryClick}>
            <AudioWaveform src={media.mediaurl} />
            <audio src={media.mediaurl} controls />
          </div>
        );
      default:
        return null;
    }
  };

  const renderThumbnailMedia = (media, index) => {
    switch (media.mediatype) {
      case "image":
        return (
          <img 
            src={media.mediaurl} 
            alt={`Thumbnail ${index + 1}`} 
            className="hs-thumbnail-media"
          />
        );
      case "video":
        return (
          <div className="hs-thumbnail-video-wrapper">
            <video 
              src={media.mediaurl} 
              className="hs-thumbnail-media"
              muted
            />
            <div className="hs-video-play-icon">▶</div>
          </div>
        );
      case "audio":
        return (
          <div className="hs-thumbnail-audio">
            <div className="hs-audio-icon">🎵</div>
            <span className="hs-audio-label">Audio</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="hs-container" onClick={handlePrimaryClick}>
      {/* Primary Media Display - 60% height */}
      <div className="hs-primary-section" onClick={handlePrimaryClick}>
        {renderPrimaryMedia(selectedMedia)}
      </div>

      {/* Secondary Thumbnails - 40% height */}
      <div className="hs-secondary-section" onClick={handlePrimaryClick}>
        {/* Left Scroll Button */}
        <button
          className="hs-scroll-button hs-scroll-left"
          onClick={(e) => scroll("left", e)}
          aria-label="Scroll left"
        >
          <ChevronLeft className="hs-chevron-icon" />
        </button>

        {/* Thumbnails Container */}
        <div 
          className="hs-thumbnails-wrapper" 
          ref={scrollContainerRef}
          onClick={handlePrimaryClick}
        >
          {medias.map((media, index) => (
            <div
              key={index}
              className={`hs-thumbnail-item ${
                selectedMedia?.mediaurl === media.mediaurl ? 'hs-thumbnail-selected' : ''
              }`}
              onClick={(e) => handleMediaClick(media, e)}
            >
              {renderThumbnailMedia(media, index)}
            </div>
          ))}
        </div>

        {/* Right Scroll Button */}
        <button
          className="hs-scroll-button hs-scroll-right"
          onClick={(e) => scroll("right", e)}
          aria-label="Scroll right"
        >
          <ChevronRight className="hs-chevron-icon" />
        </button>
      </div>
    </div>
  );
};

HorizontalScrollDisplay.propTypes = {
  medias: PropTypes.arrayOf(
    PropTypes.shape({
      mediatype: PropTypes.oneOf(["image", "audio", "video"]).isRequired,
      mediaurl: PropTypes.string.isRequired,
      alt: PropTypes.string,
    })
  ).isRequired,
  postData: PropTypes.object,
  onAddComment: PropTypes.func,
};

export default HorizontalScrollDisplay;
